import React from 'react';
import Row from '../row/row';
import Col from '../col/col';
import DateDisplay, { getFormattedTimeWindow, getTimeFormat, LayoutType } from '../date-display/date-display';
import DateWithTimeSplitted from '../../components/date-display/date-with-time-splitted';
import { MarkerData } from 'types/maps.consts';
import LocationIcon from '../../components/location-icon/location-icon';
import { validateLocation } from '../../services/validation-rules';
import moment from 'moment';
import { secondsInTimeFormat } from '../../services/filters';
import TooltipWrapper from 'components/tooltip/tooltip';
import { Location as BringgLocation } from '@bringg/types';
import { PromisedTimeWindow } from 'components/task-promised-time-window/task-promised-time-window';
import classNames from 'classnames';
import { getTOS } from '../../features';

export interface Translations {
	scheduledArrival: string;
	scheduledFor: string;
	timeWindow: string;
	eta: string;
	eta_tooltip: string;
	etl: string;
	etl_tooltip: string;
	arrived: string;
	left: string;
	etos: string;
	etos_tooltip: string;
	tos: string;
	tos_tooltip: string;
	asap: string;
	late_arrival_tooltip: string;
}

interface WayPointTimesProps {
	scheduledAt?: string;
	eta?: string;
	hourFormat: string;
	noLaterThan?: string;
	noEarlierThan?: string;
	etl?: string;
	checkinTime?: string;
	checkoutTime?: string;
	timeZone?: string;
	translations: Translations;
	useTeamTimezone?: boolean;
	scheduledArrival?: string;
	checkinLocation?: BringgLocation;
	checkoutLocation?: BringgLocation;
	setMapMarker: (marker: MarkerData) => void;
	etos?: number;
	asap?: boolean;
	promisedTimeWindow?: PromisedTimeWindow;
}

const INVALID_DATE = '--:--';

const WayPointTimes: React.FC<WayPointTimesProps> = ({
	scheduledAt,
	eta,
	noLaterThan,
	noEarlierThan,
	etl,
	hourFormat,
	checkinTime,
	checkoutTime,
	etos,
	timeZone,
	translations,
	useTeamTimezone,
	scheduledArrival,
	checkinLocation,
	checkoutLocation,
	setMapMarker,
	asap = false,
	promisedTimeWindow
}: WayPointTimesProps) => {
	const timezoneForDisplay = useTeamTimezone && timeZone;
	const TOSDuration = getTOS(checkinTime, checkoutTime);
	const showArrivedTooltip =
		moment(checkinTime).startOf('minute').isAfter(moment(noLaterThan).endOf('minute')) ||
		moment(checkinTime).startOf('minute').isAfter(moment(promisedTimeWindow?.promisedEndTime).endOf('minute'))
			? true
			: false;
	const TOOLTIP_COLOR = '#333333';

	const getTimeColumn = (title?: string, content?: React.ReactNode, dataTestId?: string, toolTipText?: string) => (
		<Col className="waypoint-time-column-container" data-test-id={dataTestId || ''}>
			{toolTipText ? (
				<TooltipWrapper color={TOOLTIP_COLOR} title={toolTipText} overlayClassName={'time-tooltip'}>
					<span className="title">{title}</span>
				</TooltipWrapper>
			) : (
				<span className="title">{title}</span>
			)}
			<div className="waypoint-time-content">{content}</div>
		</Col>
	);

	return (
		<div className="waypoint-times-container">
			<Col className="time-column left">
				{getTimeColumn(
					translations.timeWindow,
					<div>
						{getFormattedTimeWindow(
							noEarlierThan,
							noLaterThan,
							hourFormat,
							timezoneForDisplay,
							LayoutType.INLINE
						)}
					</div>,
					'time-window'
				)}
				{getTimeColumn(
					translations.scheduledFor,

					<>
						<DateWithTimeSplitted date={scheduledAt} hourFormat={hourFormat} timezone={timeZone} />
						{asap && <span className="asap"> ({translations.asap})</span>}
					</>,
					'scheduled-for'
				)}
				{scheduledArrival &&
					getTimeColumn(
						translations.scheduledArrival,
						<DateWithTimeSplitted date={scheduledArrival} hourFormat={hourFormat} timezone={timeZone} />,
						'scheduled-arrival'
					)}
			</Col>
			<Col className="time-column right">
				<Row>
					{getTimeColumn(
						translations.eta,
						<DateDisplay
							invalidDateText={INVALID_DATE}
							date={eta}
							format={getTimeFormat(hourFormat)}
							timezone={timezoneForDisplay}
						/>,
						'estimated-arrived',
						translations.eta_tooltip
					)}
					{getTimeColumn(
						translations.etl,
						<DateDisplay
							invalidDateText={INVALID_DATE}
							date={etl}
							format={getTimeFormat(hourFormat)}
							timezone={timezoneForDisplay}
						/>,
						'estimated-left',
						translations.etl_tooltip
					)}
					{getTimeColumn(
						translations.etos,
						<div className="waypoint-time-content">
							{etos >= 0 ? secondsInTimeFormat(etos) : INVALID_DATE}
						</div>,
						'ETOS',
						translations.etos_tooltip
					)}
				</Row>
				<Row>
					{getTimeColumn(
						translations.arrived,
						<div className={classNames('content-with-location ', { warning: showArrivedTooltip })}>
							<DateWithTimeSplitted date={checkinTime} hourFormat={hourFormat} timezone={timeZone} />
							{validateLocation(checkinLocation?.lng, checkinLocation?.lat) && (
								<LocationIcon
									setMapMarker={setMapMarker}
									lat={checkinLocation?.lat}
									lng={checkinLocation?.lng}
								></LocationIcon>
							)}
						</div>,
						'checkin-time',
						showArrivedTooltip ? translations.late_arrival_tooltip : ''
					)}
					{getTimeColumn(
						translations.left,
						<div className="content-with-location">
							<DateWithTimeSplitted date={checkoutTime} hourFormat={hourFormat} timezone={timeZone} />
							{validateLocation(checkoutLocation?.lng, checkoutLocation?.lat) && (
								<LocationIcon
									setMapMarker={setMapMarker}
									lat={checkoutLocation?.lat}
									lng={checkoutLocation?.lng}
								></LocationIcon>
							)}
						</div>,
						'checkout-time'
					)}
					{getTimeColumn(
						translations.tos,
						<div className={classNames({ warning: etos && TOSDuration > etos })}>
							{TOSDuration >= 0 ? secondsInTimeFormat(TOSDuration) : INVALID_DATE}
						</div>,
						'TOS',
						translations.tos_tooltip
					)}
				</Row>
			</Col>
		</div>
	);
};

export default WayPointTimes;
